import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./Table.scss";
import Input from "../../Atomsv2/Input/Input";
import Select from "../../Atomsv2/Select/Select";
import Calendar from "../../Atomsv2/Calendar/Calendar";
import Checkbox from "../../Atomsv2/Checkbox/Checkbox";
import ActionButton from "../../Atomsv2/ActionButton/ActionButton";
import Tooltip from "../../Atomsv2/Tooltip/Tooltip";
import Text from "../../Atomsv2/Text/Text";

// ─── Celda de edición inline ──────────────────────────────────────────────────
const EditCell = ({ type, value, options = [], onChange, mapper, disabled = false }) => {
    // Hooks siempre al inicio (reglas de hooks: nunca después de returns condicionales)
    const inputRef = useRef(null);
    useEffect(() => {
        if (type === "text") {
            inputRef.current?.focus({ preventScroll: true });
        }
    }, [type]);

    if (type === "checkbox") {
        return (
            <Checkbox
                checked={!!value}
                onChange={(e) => onChange(e.target.checked)}
            />
        );
    }
    if (type === "select") {
        const normalizedOptions = options.map((opt) => ({
            label: String(opt?.[mapper?.name ?? "label"] ?? opt?.name ?? opt?.label ?? "").trim(),
            value: opt?.[mapper?.value ?? "value"] ?? opt?.code ?? opt?.value,
        }));

        // Buscar la opción coincidente para pasar exactamente su .value (mismo tipo → === siempre funciona)
        const matchingOpt = normalizedOptions.find(opt =>
            String(opt.value) === String(value) || String(opt.label) === String(value)
        );

        return (
            <Select
                value={matchingOpt?.value ?? null}
                options={normalizedOptions}
                mapper={{ name: 'label', value: 'value' }}
                onChange={(opt) => onChange(opt?.value ?? null, opt?.label ?? '')}
                disabled={disabled}
                marginBottom={false}
                cleaner={true}
            />
        );
    }
    if (type === "date") {
        return (
            <Calendar
                value={value ?? ""}
                onChange={(v) => onChange(v)}
                disabled={disabled}
                classNameWrapper="edit-cell-date"
            />
        );
    }
    if (type === "datetime") {
        return (
            <Calendar
                withTime
                value={value ?? ""}
                onChange={(v) => onChange(v)}
                disabled={disabled}
                classNameWrapper="edit-cell-date"
            />
        );
    }
    if (type === "currency") {
        return (
            <Input
                type="currency"
                className="edit-cell-currency"
                value={value ?? ""}
                onChange={(e) => onChange(e.target.value)}
            />
        );
    }
    // default: text
    return (
        <input
            ref={inputRef}
            className="edit-cell-input"
            type="text"
            value={value ?? ""}
            onChange={(e) => onChange(e.target.value)}
        />
    );
};

const Table = ({
    children, data: dataProp = [], header, footer, stripedRows, globalFilter,
    paginator, rows = 10, height = "100vh", rowHeight = 44, buffer = 50,
    selectable = false, onSelectionChange,
    responsiveHideColumn = false,
    download = false,
    onRowEdit,
    onDraftChange,
    labelBuscador,
    onEditStart,
    loading = false,
    skeleton = false,
    columnSelect = null,
    showCounter = false,
}) => {
    const allColumns = React.Children.toArray(children);
    const data = Array.isArray(dataProp) ? dataProp : [];

    // --------------------------------------------------- skeleton
    const getNumericHeight = () => {
        if (typeof height === "number") return height;
        if (typeof height === "string") {
            if (height.endsWith("px")) return parseFloat(height);
            if (height.endsWith("vh")) return window.innerHeight * parseFloat(height) / 100;
        }
        return window.innerHeight;
    };
    const skeletonRowCount = skeleton
        ? Math.max(3, Math.floor((getNumericHeight() - rowHeight) / rowHeight))
        : 0;
    // --------------------------------------------------- fin skeleton

    // --------------------------------------------------- column select
    const [visibleColKeys, setVisibleColKeys] = useState(() =>
        columnSelect ? new Set(columnSelect) : null
    );
    const [colSelectOpen, setColSelectOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
    const colSelectBtnRef = useRef(null);
    const colSelectMenuRef = useRef(null);

    useEffect(() => {
        if (!colSelectOpen) return;
        const handler = (e) => {
            if (
                !colSelectBtnRef.current?.contains(e.target) &&
                !colSelectMenuRef.current?.contains(e.target)
            ) {
                setColSelectOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [colSelectOpen]);

    const toggleColKey = (key) => {
        setVisibleColKeys(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const selectableCols = allColumns.filter(col => col.props.value && col.props.header);
    const columns = (columnSelect && visibleColKeys)
        ? allColumns.filter(col => !col.props.value || visibleColKeys.has(col.props.value))
        : allColumns;
    // --------------------------------------------------- fin column select

    // --------------------------------------------------- filtro global
    const [filterValue, setFilterValue] = useState("");

    const filteredData = globalFilter && filterValue
        ? data.filter(row =>
            columns.some(col => {
                if (!col.props.value) return false;
                const val = String(row[col.props.value] ?? "").toLowerCase();
                const search = filterValue.toLowerCase();
                return val === search ||
                    val.startsWith(search) ||
                    val.split(/[\s,.\-_]+/).some(word => word.startsWith(search));
            })
        )
        : data;
    // --------------------------------------------------- fin filtro global

    // --------------------------------------------------- ordenamiento
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(1);

    const handleSort = (field) => {
        setCurrentPage(1);
        if (sortField !== field) {
            // campo nuevo → ascendente
            setSortField(field);
            setSortOrder(1);
        } else if (sortOrder === 1) {
            // ya ascendente → descendente
            setSortOrder(-1);
        } else {
            // ya descendente → resetear
            setSortField(null);
            setSortOrder(1);
        }
    };

    const sortedData = [...filteredData].sort((a, b) => {
        if (!sortField) return 0;
        const strA = String(a[sortField] ?? "");
        const strB = String(b[sortField] ?? "");
        return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" }) * sortOrder;
    });

    const getSortIcon = (field) => {
        if (sortField !== field) return <span style={{ fontSize: 'var(--icon-size)' }} className="icon" data-icon="swap_vert" />;
        return sortOrder === 1 ? <span style={{ fontSize: 'var(--icon-size)' }} className="icon" data-icon="arrow_upward" /> : <span style={{ fontSize: 'var(--icon-size)' }} className="icon" data-icon="arrow_downward" />;
    };
    // --------------------------------------------------- fin ordenamiento

    // --------------------------------------------------- paginador
    const [currentPage, setCurrentPage] = useState(1);
    const totalPages = Math.max(1, Math.ceil(sortedData.length / rows));
    const pageData = paginator
        ? sortedData.slice((currentPage - 1) * rows, currentPage * rows)
        : sortedData;
    // --------------------------------------------------- fin paginador

    // --------------------------------------------------- virtual scroll
    const [scrollTop, setScrollTop] = useState(0);
    const scrollRef = useRef(null);
    const tableRef = useRef(null);
    const visibleCount = Math.ceil((typeof height === "string" ? window.innerHeight : height) / rowHeight) + buffer;
    const startIndex = Math.max(0, Math.floor(scrollTop / rowHeight) - 2);
    const endIndex = Math.min(pageData.length, startIndex + visibleCount);
    const visibleRows = pageData.slice(startIndex, endIndex);
    const topSpacerHeight = startIndex * rowHeight;
    const bottomSpacerHeight = Math.max(0, (pageData.length - endIndex) * rowHeight);
    // --------------------------------------------------- fin virtual scroll

    // --------------------------------------------------- responsive hide column
    const [hiddenCount, setHiddenCount] = useState(0);
    const [expandedRows, setExpandedRows] = useState(new Set());
    const hiddenCountRef = useRef(0);
    const [needsHorizontalScroll, setNeedsHorizontalScroll] = useState(false);
    const needsHScrollRef = useRef(false);

    // --------------------------------------------------- edición inline (declarado aquí para que totalCols lo use)
    const editableCols = columns.filter(col => col.props.edit);
    const hasEditable  = editableCols.length > 0;

    const [editingRowIndex, setEditingRowIndex] = useState(null);
    const [draftValues,     setDraftValues]     = useState({});

    const startEdit = (rowIndex, row) => {
        const draft = {};
        editableCols.forEach(col => { draft[col.props.value] = row[col.props.value]; });
        setDraftValues(draft);
        setEditingRowIndex(rowIndex);
        // Auto-expandir la fila para que los campos editables ocultos (responsive) sean accesibles
        setExpandedRows(prev => { const next = new Set(prev); next.add(rowIndex); return next; });
        onEditStart?.(row, rowIndex, draft);
    };

    const saveEdit = (row, rowIndex) => {
        const newRow = { ...row, ...draftValues };
        const shouldKeepEditing = onRowEdit?.(newRow, rowIndex, row) === false;
        if (shouldKeepEditing) return;
        setEditingRowIndex(null);
        setDraftValues({});
        // Colapsar la fila al guardar
        setExpandedRows(prev => { const next = new Set(prev); next.delete(rowIndex); return next; });
    };

    const setDraft = (field, value) => {
        setDraftValues(prev => {
            const newDraft = { ...prev, [field]: value };
            const overrides = onDraftChange?.(field, value, newDraft);
            return overrides ? { ...newDraft, ...overrides } : newDraft;
        });
    };
    // --------------------------------------------------- fin edición inline

    const getEditDisabled = (col, row) => {
        if (!col.props.editDisabled) return false;
        if (typeof col.props.editDisabled === "function") {
            return col.props.editDisabled(row, draftValues, editingRowIndex);
        }
        return !!col.props.editDisabled;
    };

    const allResponsiveCols = columns.filter(col => col.props.responsive);

    const getCssWidth = (width) => {
        if (width === undefined || width === null || width === "") return undefined;
        if (typeof width === "number") return `${width}px`;
        return String(width);
    };

    const getColumnStyle = (col, extraStyle = {}) => {
        const cssWidth = getCssWidth(col.props.width);
        return {
            ...(cssWidth ? { width: cssWidth, minWidth: cssWidth } : {}),
            ...extraStyle,
        };
    };

    // ── Ancho natural de una columna: del prop `width` (ej: "160px") o heurística por texto ──
    const getColW = (col) => {
        const n = parseFloat(col.props.width);
        return !isNaN(n) ? n : Math.max(80, (String(col.props.header ?? "").length + 6) * 8);
    };

    // ── Referencia mutable a la función de cálculo (patrón ref para evitar stale closures) ──
    const applyResponsiveRef = useRef(null);
    applyResponsiveRef.current = () => {
        if (!responsiveHideColumn || !scrollRef.current) return;
        const containerW = scrollRef.current.clientWidth;

        // Ancho fijo de columnas de control (selección, edición)
        const fixedW = (selectable ? 48 : 0) + (hasEditable ? 48 : 0);

        // Suma de anchos de columnas no-responsive
        const nonRespW = columns
            .filter(c => !c.props.responsive)
            .reduce((s, c) => s + getColW(c), 0);

        // Anchos de columnas responsive (izquierda→derecha, la última se oculta primero)
        const respWidths = allResponsiveCols.map(getColW);

        const totalAll = nonRespW + respWidths.reduce((a, b) => a + b, 0) + fixedW;

        let needed = 0;
        if (totalAll > containerW + 2) {
            // Al ocultar columnas aparece la columna "+" (40px): añadirla al cálculo
            let t = totalAll + 40;
            for (let i = respWidths.length - 1; i >= 0 && t > containerW + 2; i--) {
                t -= respWidths[i];
                needed++;
            }
            needed = Math.min(needed, allResponsiveCols.length);
        }

        if (needed !== hiddenCountRef.current) {
            hiddenCountRef.current = needed;
            setHiddenCount(needed);
        }

        // Detectar si columnas no-responsive siguen desbordando tras ocultar todas las responsive
        const expandColW = needed > 0 ? 40 : 0;
        const nonRespOnlyW = columns
            .filter(c => !c.props.responsive)
            .reduce((s, c) => s + getColW(c), 0);
        const allWillBeHidden = needed >= allResponsiveCols.length && allResponsiveCols.length > 0;
        const newNeedsHScroll = allWillBeHidden
            ? nonRespOnlyW + fixedW + expandColW > containerW + 2
            : false;
        if (newNeedsHScroll !== needsHScrollRef.current) {
            needsHScrollRef.current = newNeedsHScroll;
            setNeedsHorizontalScroll(newNeedsHScroll);
        }
    };

    // Verificación inicial síncrona (evita flash de columnas antes de ocultarlas)
    useLayoutEffect(() => {
        applyResponsiveRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ResizeObserver: re-calcula cuando cambia el tamaño del contenedor
    useEffect(() => {
        if (!responsiveHideColumn || !scrollRef.current) return;
        const el = scrollRef.current;
        const observer = new ResizeObserver(() => applyResponsiveRef.current());
        observer.observe(el);
        return () => observer.disconnect();
    }, [responsiveHideColumn]);

    // Ajuste fino: si tras el cálculo heurístico aún hay overflow real,
    // oculta columnas responsive adicionales hasta que deje de desbordar.
    // Cuando ya no quedan responsive por ocultar y sigue habiendo overflow → scroll horizontal.
    useLayoutEffect(() => {
        if (!responsiveHideColumn || !scrollRef.current || !tableRef.current) return;

        const containerW = scrollRef.current.clientWidth;
        const tableW = tableRef.current.scrollWidth;
        const hasOverflow = tableW > containerW + 1;

        if (hasOverflow && hiddenCount < allResponsiveCols.length) {
            const nextHidden = Math.min(allResponsiveCols.length, hiddenCount + 1);
            if (nextHidden !== hiddenCountRef.current) {
                hiddenCountRef.current = nextHidden;
                setHiddenCount(nextHidden);
            }
        }

        // Scroll horizontal cuando todas las responsive ya están ocultas y aún hay overflow
        const allHidden = hiddenCount >= allResponsiveCols.length;
        const newNeedsHScroll = hasOverflow && allHidden;
        if (newNeedsHScroll !== needsHScrollRef.current) {
            needsHScrollRef.current = newNeedsHScroll;
            setNeedsHorizontalScroll(newNeedsHScroll);
        }
    }, [responsiveHideColumn, hiddenCount, allResponsiveCols.length, columns.length]);

    // Derivar columnas visibles y ocultas (la última responsive se oculta primero)
    const responsiveActive = responsiveHideColumn && hiddenCount > 0;
    const visibleColumns = (() => {
        if (!responsiveHideColumn || hiddenCount === 0) return columns;
        return columns.filter(col => {
            if (!col.props.responsive) return true;
            const rIdx = allResponsiveCols.indexOf(col);
            return rIdx < allResponsiveCols.length - hiddenCount;
        });
    })();
    const hiddenColumns = (() => {
        if (!responsiveHideColumn || hiddenCount === 0) return [];
        return columns.filter(col => {
            if (!col.props.responsive) return false;
            const rIdx = allResponsiveCols.indexOf(col);
            return rIdx >= allResponsiveCols.length - hiddenCount;
        });
    })();
    const totalCols =
        visibleColumns.length +
        (selectable ? 1 : 0) +
        (responsiveActive ? 1 : 0) +
        (hasEditable ? 1 : 0);

    const toggleExpand = (rowIndex) => {
        setExpandedRows(prev => {
            const next = new Set(prev);
            next.has(rowIndex) ? next.delete(rowIndex) : next.add(rowIndex);
            return next;
        });
    };
    // --------------------------------------------------- fin responsive hide column

    // --------------------------------------------------- selección
    const [selectedRows, setSelectedRows] = useState(new Set());

    const allFilteredSelected =
        filteredData.length > 0 && filteredData.every(row => selectedRows.has(row));
    const someFilteredSelected =
        !allFilteredSelected && filteredData.some(row => selectedRows.has(row));

    const toggleRow = (row) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            next.has(row) ? next.delete(row) : next.add(row);
            onSelectionChange?.(Array.from(next));
            return next;
        });
    };

    const toggleAll = () => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            if (allFilteredSelected || someFilteredSelected) {
                filteredData.forEach(row => next.delete(row));
            } else {
                filteredData.forEach(row => next.add(row));
            }
            onSelectionChange?.(Array.from(next));
            return next;
        });
    };
    // --------------------------------------------------- fin selección

    // --------------------------------------------------- descarga excel
    const downloadConfig = download === true ? {} : (download || null);
    const downloadEnabled = download === true || (download && typeof download === "object");
    const [isDownloading, setIsDownloading] = useState(false);

    const handleDownload = async () => {
        if (isDownloading) return;
        setIsDownloading(true);
        try {
            const ExcelJS = (await import("exceljs")).default;

            // columnas a incluir
            const allValueCols = columns.filter(col => col.props.value);
            const targetCols = downloadConfig?.columns
                ? allValueCols.filter(col => downloadConfig.columns.includes(col.props.value))
                : allValueCols;

            // datos a incluir
            const targetData = downloadConfig?.onlyFilter
                ? sortedData
                : [...data].sort((a, b) => {
                    if (!sortField) return 0;
                    const strA = String(a[sortField] ?? "");
                    const strB = String(b[sortField] ?? "");
                    return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" }) * sortOrder;
                });

            // leer variables CSS en tiempo de ejecución para que sigan al tema
            const cssVar = (name) => {
                const raw = getComputedStyle(document.documentElement)
                    .getPropertyValue(name).trim();
                // normalizar a RRGGBB (soporta #rgb y #rrggbb)
                const hex = raw.startsWith("#") ? raw.slice(1) : raw;
                const full = hex.length === 3
                    ? hex.split("").map(c => c + c).join("")
                    : hex;
                return "FF" + full.toUpperCase(); // argb con alpha FF (opaco)
            };

            const colorHeaderBg   = cssVar("--table-header-bg");
            const colorHeaderFont = cssVar("--gray-dark-500");
            const colorBorder     = cssVar("--table-border");
            const colorStripe     = cssVar("--table-row-stripe");
            const colorFont       = cssVar("--gray-dark-700");
            const colorRowBg      = "FFFFFFFF"; // blanco puro

            const border = (argb) => ({ style: "thin", color: { argb } });
            const cellBorder = (argb) => ({
                top: border(argb), left: border(argb),
                bottom: border(argb), right: border(argb),
            });

            const workbook = new ExcelJS.Workbook();
            const sheet = workbook.addWorksheet("Datos");

            // anchos de columna adaptados al contenido
            sheet.columns = targetCols.map(col => {
                const headerLen = String(col.props.header ?? "").length;
                const maxDataLen = targetData.length > 0
                    ? Math.min(50, Math.max(...targetData.map(row =>
                        String(row[col.props.value] ?? "").length
                    )))
                    : 10;
                return {
                    header: String(col.props.header ?? "").toUpperCase(),
                    key: col.props.value,
                    width: Math.max(headerLen + 4, maxDataLen + 2, 12),
                };
            });

            // estilos fila de encabezado
            const headerRow = sheet.getRow(1);
            headerRow.height = 28;
            headerRow.eachCell(cell => {
                cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: colorHeaderBg } };
                cell.font      = { bold: true, size: 9, color: { argb: colorHeaderFont } };
                cell.border    = cellBorder(colorBorder);
                cell.alignment = { vertical: "middle", horizontal: "left" };
            });

            // filas de datos
            targetData.forEach((row, i) => {
                const rowValues = {};
                targetCols.forEach(col => {
                    rowValues[col.props.value] = row[col.props.value] ?? "";
                });
                const excelRow = sheet.addRow(rowValues);
                const isStriped = stripedRows && i % 2 !== 0;
                excelRow.height = 22;
                excelRow.eachCell({ includeEmpty: true }, cell => {
                    cell.fill      = { type: "pattern", pattern: "solid", fgColor: { argb: isStriped ? colorStripe : colorRowBg } };
                    cell.border    = cellBorder(colorBorder);
                    cell.font      = { size: 10, color: { argb: colorFont } };
                    cell.alignment = { vertical: "middle" };
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob   = new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url  = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href     = url;
            const fileName = downloadConfig?.name
                ? `${downloadConfig.name}.xlsx`
                : "datos.xlsx";
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } finally {
            setIsDownloading(false);
        }
    };
    // --------------------------------------------------- fin descarga excel

    return (
        <div className="table-wrapper">
            {(header || globalFilter || downloadEnabled || columnSelect || showCounter) && (
                <div className="table-toolbar">
                    <div className="table-toolbar-left">
                        {header && <div className="table-toolbar-title">{header}</div>}
                        {globalFilter && (
                            <Input
                                label={labelBuscador}
                                className="global-filter-input"
                                placeholder="Buscar..."
                                value={filterValue}
                                onChange={(e) => { setFilterValue(e.target.value); setCurrentPage(1); }}
                                icon="search"
                            />
                        )}
                    </div>
                    {/* ----------------------------------------------------------acciones de la tabla */}
                    <div className="table-toolbar-right">
                        {showCounter && (
                            <Text size="extra-small" color="var(--gray-900)">
                                {filterValue
                                    ? `${filteredData.length} de ${data.length} registros`
                                    : `${data.length} registros`}
                            </Text>
                        )}
                        {columnSelect && (
                            <div className="col-select-dropdown" ref={colSelectBtnRef}>
                                <ActionButton
                                    icon="view_column"
                                    title="Columnas visibles"
                                    onClick={() => {
                                        if (!colSelectOpen && colSelectBtnRef.current) {
                                            const rect = colSelectBtnRef.current.getBoundingClientRect();
                                            setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                                        }
                                        setColSelectOpen(o => !o);
                                    }}
                                />
                            </div>
                        )}
                        {columnSelect && colSelectOpen && createPortal(
                            <div
                                ref={colSelectMenuRef}
                                className="col-select-menu"
                                style={{ position: "fixed", top: menuPos.top, right: menuPos.right, zIndex: 9999 }}
                            >
                                {selectableCols.map(col => (
                                    <label key={col.props.value} className="col-select-item">
                                        <input
                                            type="checkbox"
                                            checked={visibleColKeys?.has(col.props.value) ?? true}
                                            onChange={() => toggleColKey(col.props.value)}
                                        />
                                        {col.props.tooltipHeader || col.props.header}
                                    </label>
                                ))}
                            </div>,
                            document.body
                        )}
                        {downloadEnabled && (
                            <ActionButton
                                className={isDownloading ? "hoverActionTableLoadinng" : ""}
                                icon={isDownloading ? "hourglass_empty" : "download"}
                                title="Descargar reporte"
                                onClick={handleDownload}
                                disabled={isDownloading}
                            />
                        )}
                    </div>
                </div>
            )}
            
            
            <div
                ref={scrollRef}
                className={`table-scroll-container${responsiveHideColumn ? " table-responsive-mode" : ""}${needsHorizontalScroll ? " table-has-hscroll" : ""}`}
                style={{
                    height: height,
                    overflowY: "auto",
                    overflowX: responsiveHideColumn && !needsHorizontalScroll ? "hidden" : "auto",
                }}
                onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
            >
                <table className="Table" ref={tableRef}>
                    <thead>
                        <tr>
                            {responsiveActive && <th className="col-control" />}
                            {selectable && (
                                <th className="col-select">
                                    <Checkbox
                                        checked={allFilteredSelected}
                                        indeterminate={someFilteredSelected}
                                        onChange={toggleAll}
                                    />
                                </th>
                            )}
                            {visibleColumns.map((col, index) => (
                                <th
                                    key={index}
                                    onClick={col.props.sortable ? () => handleSort(col.props.value) : undefined}
                                    style={getColumnStyle(
                                        col,
                                        col.props.sortable ? { cursor: "pointer", userSelect: "none" } : {}
                                    )}
                                >
                                    <div className={`container-th ${col.props.classNameHeader || ""}`}>
                                        {col.props.tooltipHeader ? (
                                            <Tooltip content={col.props.tooltipHeader}>
                                                {col.props.header}
                                            </Tooltip>
                                        ) : (
                                            col.props.header
                                        )}
                                        {col.props.sortable && getSortIcon(col.props.value)}
                                    </div>
                                </th>
                            ))}
                            {hasEditable && <th className="col-edit" />}
                        </tr>
                    </thead>
                    <tbody>
                        {skeleton ? (
                            Array.from({ length: skeletonRowCount }, (_, skRow) => (
                                <tr key={`sk-${skRow}`} className="skeleton-row" style={{ height: rowHeight }}>
                                    {responsiveActive && <td className="col-control" />}
                                    {selectable && (
                                        <td className="col-select">
                                            <div className="skeleton-line skeleton-line--circle" />
                                        </td>
                                    )}
                                    {visibleColumns.map((col, ci) => (
                                        <td key={ci} style={getColumnStyle(col)}>
                                            <div className="skeleton-line" />
                                        </td>
                                    ))}
                                    {hasEditable && <td className="col-edit" />}
                                </tr>
                            ))
                        ) : (
                            <>
                            {topSpacerHeight > 0 && (
                                <tr className="row-spacer" style={{ height: topSpacerHeight }}><td colSpan={totalCols || columns.length} /></tr>
                            )}
                            {visibleRows.map((row, i) => {
                            const rowIndex = startIndex + i;
                            const isExpanded = expandedRows.has(rowIndex);
                            return (
                                <React.Fragment key={rowIndex}>
                                    <tr
                                        className={[
                                            stripedRows && rowIndex % 2 !== 0 ? "row-striped" : "",
                                            selectable && selectedRows.has(row) ? "row-selected" : "",
                                        ].filter(Boolean).join(" ")}
                                    >
                                        {responsiveActive && (
                                            <td className="col-control">
                                                <button
                                                    className={`row-expand-btn ${isExpanded ? "expanded" : ""}`}
                                                    onClick={() => toggleExpand(rowIndex)}
                                                >
                                                    <span className="icon">{isExpanded ? "remove" : "add"}</span>
                                                </button>
                                            </td>
                                        )}
                                        {selectable && (
                                            <td className="col-select">
                                                <Checkbox
                                                    checked={selectedRows.has(row)}
                                                    onChange={() => toggleRow(row)}
                                                />
                                            </td>
                                        )}
                                        {visibleColumns.map((col, colIndex) => {
                                            const isEditing  = editingRowIndex === rowIndex;
                                            const canEdit    = col.props.edit &&
                                                (!col.props.editCondition || col.props.editCondition(row));
                                            const showInput  = isEditing && canEdit;

                                            return (
                                                <td
                                                    key={colIndex}
                                                    className={showInput ? "cell-editing" : ""}
                                                    style={getColumnStyle(
                                                        col,
                                                        {
                                                            ...(col.props.align ? { textAlign: col.props.align } : {}),
                                                            ...(col.props.onClick ? { cursor: "pointer" } : {}),
                                                        }
                                                    )}
                                                    onClick={col.props.onClick && !showInput
                                                        ? () => col.props.onClick(row, rowIndex)
                                                        : undefined
                                                    }
                                                >
                                                    {showInput ? (
                                                        <EditCell
                                                            type={col.props.edit}
                                                            value={draftValues[col.props.value]}
                                                            options={col.props.editOptions}
                                                            mapper={col.props.editMapper}
                                                            disabled={getEditDisabled(col, row)}
                                                            onChange={(v, label) => {
                                                                if (col.props.editLabelKey && label !== undefined) {
                                                                    setDraftValues(prev => {
                                                                        const d = { ...prev, [col.props.value]: v, [col.props.editLabelKey]: label };
                                                                        const overrides = onDraftChange?.(col.props.value, v, d);
                                                                        return overrides ? { ...d, ...overrides } : d;
                                                                    });
                                                                } else {
                                                                    setDraft(col.props.value, v);
                                                                }
                                                            }}
                                                        />
                                                    ) : (
                                                        col.props.body ? col.props.body(row) : row[col.props.value]
                                                    )}
                                                </td>
                                            );
                                        })}
                                        {hasEditable && (() => {
                                            const isEditing    = editingRowIndex === rowIndex;
                                            const rowCanEdit   = editableCols.some(col =>
                                                !col.props.editCondition || col.props.editCondition(row)
                                            );
                                            if (!rowCanEdit) return <td className="col-edit" />;
                                            return (
                                                <td className="col-edit">
                                                    {isEditing ? (
                                                        <ActionButton
                                                            icon="check"
                                                            title="Guardar cambios"
                                                            className="edit-save-btn"
                                                            onClick={() => saveEdit(row, rowIndex)}
                                                        />
                                                    ) : (
                                                        editingRowIndex === null && (
                                                            <ActionButton
                                                                icon="edit"
                                                                title="Editar fila"
                                                                className="edit-start-btn"
                                                                onClick={() => startEdit(rowIndex, row)}
                                                            />
                                                        )
                                                    )}
                                                </td>
                                            );
                                        })()}
                                    </tr>
                                    {responsiveActive && isExpanded && hiddenColumns.length > 0 && (
                                        <tr className="row-child">
                                            <td colSpan={totalCols} className="row-child-cell">
                                                <div className="row-child-content">
                                                    {hiddenColumns.map((col, ci) => {
                                                        const isEditingRow = editingRowIndex === rowIndex;
                                                        const canEdit = col.props.edit &&
                                                            (!col.props.editCondition || col.props.editCondition(row));
                                                        return (
                                                            <div key={ci} className={`row-child-item${isEditingRow && canEdit ? " row-child-item--editing" : ""}`}>
                                                                <span className="row-child-label">{col.props.header}</span>
                                                                <span
                                                                    className="row-child-value"
                                                                    style={col.props.onClick ? { cursor: "pointer" } : undefined}
                                                                    onClick={col.props.onClick && !isEditingRow
                                                                        ? () => col.props.onClick(row, rowIndex)
                                                                        : undefined
                                                                    }
                                                                >
                                                                    {isEditingRow && canEdit ? (
                                                                        <EditCell
                                                                            type={col.props.edit}
                                                                            value={draftValues[col.props.value]}
                                                                            options={col.props.editOptions ?? []}
                                                                            mapper={col.props.editMapper}
                                                                            disabled={getEditDisabled(col, row)}
                                                                            onChange={(v, label) => {
                                                                                if (col.props.editLabelKey && label !== undefined) {
                                                                                    setDraftValues(prev => {
                                                                                        const d = { ...prev, [col.props.value]: v, [col.props.editLabelKey]: label };
                                                                                        const overrides = onDraftChange?.(col.props.value, v, d);
                                                                                        return overrides ? { ...d, ...overrides } : d;
                                                                                    });
                                                                                } else {
                                                                                    setDraft(col.props.value, v);
                                                                                }
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        col.props.body ? col.props.body(row) : row[col.props.value]
                                                                    )}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                        {bottomSpacerHeight > 0 && (
                            <tr className="row-spacer" style={{ height: bottomSpacerHeight }}><td colSpan={totalCols || columns.length} /></tr>
                        )}
                            </>
                        )}
                    </tbody>
                </table>
            </div>
            {paginator && (() => {
                const showingFrom = sortedData.length === 0 ? 0 : (currentPage - 1) * rows + 1;
                const showingTo = Math.min(currentPage * rows, sortedData.length);
                const total = sortedData.length;

                const getPages = () => {
                    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
                    const pages = [1];
                    if (currentPage > 3) pages.push("...");
                    for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
                        pages.push(i);
                    }
                    if (currentPage < totalPages - 2) pages.push("...");
                    pages.push(totalPages);
                    return pages;
                };

                return (
                    <div className="table-paginator">
                        <span className="paginator-info">
                            Mostrando <strong>{showingFrom}–{showingTo}</strong> de <strong>{total.toLocaleString()}</strong>
                        </span>
                        <div className="paginator-pages">
                            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                                <span className="icon">chevron_left</span>
                            </button>
                            {getPages().map((page, i) =>
                                page === "..." ? (
                                    <button key={`ellipsis-${i}`} className="page-ellipsis" disabled>…</button>
                                ) : (
                                    <button
                                        key={page}
                                        onClick={() => setCurrentPage(page)}
                                        className={currentPage === page ? "page-active" : ""}
                                    >
                                        {page}
                                    </button>
                                )
                            )}
                            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                                <span className="icon">chevron_right</span>
                            </button>
                        </div>
                    </div>
                );
            })()}
            {footer && <div className="table-footer">{footer}</div>}
        </div>
    );
};

export default Table;